import React, { useEffect, useState } from 'react';
import { Card, Stat, Table, Notice, Empty, Pill } from './ui';
import { getJson, fmtNum, pct } from './api';

/** Counts only: how the review queue moves and how timely the approved universes are. */
export default function AnalyticsView() {
  const [summary, setSummary] = useState(null);
  const [odag, setOdag] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    getJson('/analytics/summary').then((d) => setSummary(d.summary || {})).catch((e) => setError(e.message));
    getJson('/analytics/odag').then((d) => setOdag(d.data || null)).catch(() => setOdag(null));
  }, []);
  const protocols = Object.entries(summary || {});
  const totals = protocols.reduce((a, [, s]) => ({ submitted: a.submitted + (s.submitted || 0) + (s.resubmitted || 0), approved: a.approved + (s.approved || 0), changes: a.changes + (s.changes_requested || 0), rows: a.rows + (s.rows || 0), errors: a.errors + (s.errors || 0) }), { submitted: 0, approved: 0, changes: 0, rows: 0, errors: 0 });
  return (
    <div className="space-y-4">
      {error && <Notice tone="red">{error}</Notice>}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Awaiting review" value={fmtNum(totals.submitted)} tone="amber" />
        <Stat label="Approved" value={fmtNum(totals.approved)} tone="emerald" />
        <Stat label="Changes requested" value={fmtNum(totals.changes)} tone="rose" />
        <Stat label="Rows validated" value={fmtNum(totals.rows)} />
        <Stat label="Issues found" value={fmtNum(totals.errors)} />
      </div>
      <Card title="By protocol" subtitle="Submissions by status, with the volume and the error mix." padding="p-0" shadow="shadow-md">
        {!summary ? <div className="py-12 text-center text-slate-500">Loading…</div> : protocols.length === 0 ? <div className="py-12 text-center text-slate-500">No submissions yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left">{['Protocol', 'Pending', 'Changes requested', 'Approved', 'Rows', 'Issues', 'Days to approval', 'Error mix'].map((h, i) => <th key={h} className={`py-3 px-3 text-xs font-semibold text-slate-600 whitespace-nowrap ${i >= 1 && i <= 6 ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {protocols.map(([p, s]) => (
                  <tr key={p} className="align-top">
                    <td className="px-3 py-3 font-semibold text-slate-900">{p}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum((s.submitted || 0) + (s.resubmitted || 0))}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(s.changes_requested)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(s.approved)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(s.rows)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtNum(s.errors)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{s.avgDaysToApproval != null ? Number(s.avgDaysToApproval).toFixed(1) : ''}</td>
                    <td className="px-3 py-3"><div className="flex flex-wrap gap-1">{Object.entries(s.errorTypes || {}).filter(([, n]) => n > 0).map(([k, n]) => <Pill key={k}>{k} · {fmtNum(n)}</Pill>)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {odag && odag.overall && (
        <Card title="ODAG timeliness across approved universes" subtitle="From the deidentified timeliness derivative of every approved ODAG submission.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="Timely rate" value={odag.overall.timelyPct != null ? pct(odag.overall.timelyPct) : 'n/a'} tone={odag.overall.timelyPct >= 95 ? 'emerald' : 'amber'} />
            <Stat label="Timely cases" value={fmtNum(odag.overall.timelyCount)} />
            <Stat label="Untimely cases" value={fmtNum(odag.overall.untimelyCount ?? odag.overall.lateCount)} tone="rose" />
            <Stat label="Universes" value={fmtNum(odag.overall.submissions ?? odag.overall.universes)} />
          </div>
          {Array.isArray(odag.byTable || odag.tables) && (odag.byTable || odag.tables).length > 0 && (
            <Table flat head={['Table', { label: 'Timely', right: true }, { label: 'Untimely', right: true }, { label: 'Rate', right: true }]}>
              {(odag.byTable || odag.tables).map((t, i) => {
                const o = t.overall || t;
                const timely = o.timelyCount ?? o.timely;
                const total = o.total ?? ((o.timelyCount ?? 0) + (o.untimelyCount ?? 0));
                const untimely = o.untimelyCount ?? o.untimely ?? o.lateCount ?? (total != null && timely != null ? total - timely : null);
                const rate = o.timelyPct ?? o.rate ?? (total ? Math.round((timely / total) * 1000) / 10 : null);
                return <tr key={i}><td className="px-3 py-2">{t.table || t.tableLabel || t.label || t.name || t.universeType || `Table ${i + 1}`}</td><td className="px-3 py-2 text-right tabular-nums">{fmtNum(timely)}</td><td className="px-3 py-2 text-right tabular-nums">{fmtNum(untimely)}</td><td className="px-3 py-2 text-right">{rate != null ? pct(rate) : ''}</td></tr>;
              })}
            </Table>
          )}
        </Card>
      )}
      {!odag && <Empty>ODAG timeliness appears here once approved ODAG universes exist.</Empty>}
    </div>
  );
}
